import title from "global/constants/TitleConstants";
import { useLocation } from "react-router-dom";
import { useTitle } from "utils/UseTitle";
import Step2 from "./components/Step2";
import Step1 from "./components/Step1";
import { useEffect, useState } from "react";
import { CampaignContext } from "../Context";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { Container, Skeleton, Stack } from "@mui/material";
import { SubmitCampaign } from "types/Campaign";
import { initialSubmitCampaignFields } from "../Constants";
import {
  draftCampaign,
  fetchApprovedDomains,
  getWholeCampaignDetails,
} from "screens/Campaigns/CampaignService";
import {
  convertESTtoUserLocalDateAndTime,
  handleErrorMessage,
  openErrorNotification,
  openInfoNotification,
} from "helpers/methods";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import moment from "moment";

const RegularCampaign = () => {
  useTitle(title.CREATE_REGULAR_CAMPAIGN);
  const urlParams = new URLSearchParams(useLocation().search);
  const campaignId = urlParams.get("id");
  const [submitCampaign, setSubmitCampaign] = useState<SubmitCampaign>(
    initialSubmitCampaignFields("Regular") as SubmitCampaign
  );
  const [skipStepOne, setSkipStepOne] = useState<boolean>(false);
  const [managingStep, setManagingStep] = useState<string>("");
  const [loadingCampaignInfo, setLoadingCampaignInfo] =
    useState<boolean>(false);

  useEffect(() => {
    if (campaignId) {
      retrieveCampaignDetails();
    } else {
      updateDomainDetails();
    }
  }, [campaignId]);

  const retrieveCampaignDetails = async () => {
    try {
      setLoadingCampaignInfo(true);
      const response = await getWholeCampaignDetails(parseInt(campaignId!));
      const campaign = initialSubmitCampaignFields("Regular", response);
      campaign.campaign.contentJson =
        typeof campaign.campaign.contentJson === "object"
          ? campaign.campaign.contentJson
          : JSON.parse(campaign?.campaign?.contentJson ?? {});
      if (!campaign.campaignData?.domainName) {
        const domain = await updateDomainDetails();
        campaign.campaignData.domainName = domain[0];
      }
      setSubmitCampaign(campaign);
      const scheduleTime = moment(response?.campaign?.scheduleTime).format(
        "MM/DD/YYYY HH:mm:ss"
      );

      if (
        response.campaignData?.messageId &&
        response.campaignData.messageId.trim() !== ""
      ) {
        history.push(viewpaths.campaignsViewPath);
        openInfoNotification(
          "Your campaign cannot be edited because it is already in the sending queue."
        );
      } else if (
        response.campaign?.status === "Submitted" &&
        moment(
          convertESTtoUserLocalDateAndTime(
            scheduleTime,
            "MM/DD/YYYY HH:mm:ss",
            "MM/DD/YYYY HH:mm:ss"
          )
        ).isSameOrBefore(moment().format("MM/DD/YYYY HH:mm:ss"))
      ) {
        history.push(viewpaths.campaignsViewPath);
        openInfoNotification(
          "Your campaign cannot be edited because it is already in the sending queue."
        );
        return;
      }
      setSkipStepOne(true);
      setLoadingCampaignInfo(false);
    } catch (error: any) {
      setLoadingCampaignInfo(false);
      handleErrorMessage(error);
    }
  };

  const updateDomainDetails = async () => {
    try {
      const response = await fetchApprovedDomains();
      if (response.length === 1) {
        setSubmitCampaign({
          ...submitCampaign,
          campaignData: {
            ...submitCampaign.campaignData,
            domainName: response[0],
          },
        });
        return response;
      }
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const saveAsDraft = async (submitCampaign: SubmitCampaign) => {
    try {
      const campaignId =
        submitCampaign.campaign.status === "Draft" &&
        (await draftCampaign(submitCampaign, ""));
      return campaignId;
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const getSteps = () => {
    if (loadingCampaignInfo) {
      return (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Skeleton animation="wave" variant="text" height={45} width={200} />
            <Skeleton animation="wave" variant="text" height={45} width={200} />
          </Stack>
          <Skeleton animation="wave" variant="rounded" height={125} />
          <Skeleton animation="wave" variant="rounded" height={125} />
        </Stack>
      );
    }
    if (skipStepOne) {
      return <Step2 />;
    } else {
      return <Step1 setSkipStepOne={setSkipStepOne} />;
    }
  };

  return (
    <CampaignContext.Provider
      value={{
        submitCampaign,
        setSubmitCampaign,
        managingStep,
        setManagingStep,
        saveAsDraft,
      }}
    >
      <CenteredAuthenticatedLayout>
        <Container maxWidth="md">{getSteps()}</Container>
      </CenteredAuthenticatedLayout>
    </CampaignContext.Provider>
  );
};

export default RegularCampaign;
